import { useRef, useCallback, useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import throttle from "lodash-es/throttle";
import { Scene } from "@babylonjs/core/scene";
import { AppDispatch, RootState } from "app/store";
import { MapModel } from "common/model";
import { CommandManager } from "features/builder/engine";
import { PieceListContext } from "features/builder/pieceList";
import { shootBuilderScene } from "features/builder/captureThumbnail";
import { actions, mapModelAdapter, currentPieceMeta } from "features/builder/slice";
import {
  updatePieceMeta,
  appendEditHistory,
  updatePieceHints,
  updatePieceCommands,
} from "features/builder/api";
import {
  ActionRecord,
  ActionCommand,
  PieceEditMeta,
  PieceMilestone,
  ActionCommandType,
} from "features/builder/types";

// 一覧トップの縦スクロール位置を redux に保存／復元するフック。
// ready（pieces ロード完了）後にスクロール要素へ復元し、以後は throttle で保存する。
// callback ref で attach 時に復元するため、くわしく往復・タブ往復の再マウントでも効く。
export const usePieceListScroll = (context: PieceListContext, ready: boolean) => {
  const dispatch = useDispatch<AppDispatch>();
  const scrollTop = useSelector((state: RootState) => state.builder.listUi[context].scrollTop);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const savedTopRef = useRef(scrollTop);
  const readyRef = useRef(ready);
  const restoredRef = useRef(false);

  useEffect(() => {
    savedTopRef.current = scrollTop;
  }, [scrollTop]);
  useEffect(() => {
    readyRef.current = ready;
  }, [ready]);

  // ノードが既に attach 済みで ready になった瞬間に復元（初回ロード完了時）
  useEffect(() => {
    if (ready && !restoredRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = savedTopRef.current;
      restoredRef.current = true;
    }
  }, [ready]);

  // attach 時の復元（再マウント対応）。ref は安定（依存なし、ref 経由で最新値参照）。
  const ref = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    if (node && readyRef.current) {
      node.scrollTop = savedTopRef.current;
      restoredRef.current = true;
    }
  }, []);

  const saveRef = useRef(
    throttle((top: number) => dispatch(actions.setListScroll({ context, scrollTop: top })), 200, {
      leading: false,
      trailing: true,
    }),
  );

  const onScroll = useCallback(() => {
    const node = scrollRef.current;
    // 復元前の初期 0 で保存値を上書きしない
    if (!node || !restoredRef.current) return;
    saveRef.current(node.scrollTop);
  }, []);

  return { ref, onScroll, scrollRef };
};

/** キーの並びに左右されない文字列（サーバーの JSON 列はキーの順番を並べ替えて返す） */
const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableJson(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
};

/** マップと大きさが同じか（置いた順番には左右されない） */
const sameState = (aMap: MapModel[], aSize: unknown, bMap: MapModel[], bSize: unknown) => {
  const key = (models: MapModel[]) =>
    stableJson([...models].sort((x, y) => (x.id < y.id ? -1 : x.id > y.id ? 1 : 0)));
  return key(aMap) === key(bMap) && stableJson(aSize) === stableJson(bSize);
};

const MILESTONES: string[] = Object.values(PieceMilestone);

/**
 * 前の区切りから中身が変わっていなければ残さない区切り（開き直す・テストし直すたびに増えないように。
 * じゆうにつくるも同じ中身なら残さない）
 */
const SKIP_IF_UNCHANGED: string[] = [
  PieceMilestone.OPEN,
  PieceMilestone.CLOSE,
  PieceMilestone.TEST,
  PieceMilestone.AUTO,
  PieceMilestone.BEFORE_RESTORE,
];

/** 比べるための なまえなど（上限なしは API が null、画面が {} で持つので そろえる） */
const metaKey = (meta: PieceEditMeta) =>
  stableJson({
    ...meta,
    cover: meta.cover ?? null,
    background: meta.background ?? null,
    hint: [meta.hint[0] ?? "", meta.hint[1] ?? "", meta.hint[2] ?? ""],
    command_limits:
      meta.command_limits && Object.keys(meta.command_limits).length > 0
        ? meta.command_limits
        : null,
  });

/**
 * いまの中身（マップ・大きさ・なまえなど）が一番新しい区切りと同じか。区切りがまだ無ければ undefined。
 * なまえなどを持たない区切り（capture 列の前の行）は、マップと大きさだけで比べる
 */
const sameAsLastMilestone = (state: RootState): boolean | undefined => {
  const { builder } = state;
  const last = [...builder.editHistory].reverse().find((e) => MILESTONES.includes(e.action_type));
  if (!last?.map) return undefined;
  const map = mapModelAdapter.getSelectors().selectAll(builder.map);
  if (!sameState(last.map, last.size, map, builder.size)) return false;
  return !last.meta || metaKey(last.meta) === metaKey(currentPieceMeta(builder));
};

/** この区切りを残すか（とちゅうの きろくは、前の区切りがあって中身が変わったときだけ） */
const shouldRecordMilestone = (state: RootState, action_type: string): boolean => {
  if (!SKIP_IF_UNCHANGED.includes(action_type)) return true;
  const same = sameAsLastMilestone(state);
  if (action_type === PieceMilestone.AUTO) return same === false;
  return same !== true;
};

/**
 * 記録に 1 件足す（Redux にすぐ反映し、サーバーへ送る）。そのときのマップを restore 用に残す。
 * 操作・undo / redo・区切りのどれもここを通す（docs/edit-history-undo.md）。
 * capture は区切りのときの画面（りれきのカードに出す）
 */
export const recordEditHistory =
  (action_type: string, payload?: Record<string, unknown>, capture?: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    if (!shouldRecordMilestone(state, action_type)) return;
    const { builder, user } = state;
    const map = mapModelAdapter.getSelectors().selectAll(builder.map);
    const size = builder.size;
    const last = builder.editHistory.at(-1);
    // 区切りには、いまの なまえなどを残す（restore で戻す）
    const meta = MILESTONES.includes(action_type) ? currentPieceMeta(builder) : undefined;
    dispatch(
      actions.pushEditHistory({
        seq: (last?.seq ?? 0) + 1,
        version: last?.version ?? 1,
        action_type,
        payload: payload as Record<string, string | number | boolean> | undefined,
        map,
        size,
        capture: capture ?? null,
        meta: meta ?? null,
        created_at: new Date().toISOString(),
      }),
    );
    const uid = user.active?.uid;
    const pid = builder.editingBuilderMapId;
    if (uid && pid && pid !== "local-map") {
      appendEditHistory({
        piece_id: pid,
        uid,
        actions: [{ action_type, payload, map, size, capture, meta }],
      }).catch(() => undefined);
    }
  };

/**
 * いまの画面を撮って区切りを残す（テストした・もどす まえ・とちゅうの きろく、と
 * 画面を作り直したあとの 開いた・AIでつくった・もどした）。撮るのは残すときだけ。
 * 撮り終えてから記録に送るので、呼び出し側は待ってから画面を離れる・中身を変える
 */
export const recordMilestoneWithCapture =
  (scene: Scene | null, action_type: PieceMilestone, payload?: Record<string, unknown>) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    if (!shouldRecordMilestone(state, action_type)) return;
    const uid = state.user.active?.uid;
    const pid = state.builder.editingBuilderMapId;
    const capture =
      uid && pid && pid !== "local-map"
        ? await shootBuilderScene(scene, uid).catch(() => undefined)
        : undefined;
    dispatch(recordEditHistory(action_type, payload, capture));
  };

/**
 * restore・その undo / redo で戻した なまえ・せつめい・カバー・はいけい・ヒント・つかえる コマンドを保存する
 * （マップは savePieceContentThrottled が保存する）
 */
export const savePieceMeta =
  (meta: PieceEditMeta) => async (_dispatch: AppDispatch, getState: () => RootState) => {
    const { builder, user } = getState();
    const uid = user.active?.uid;
    const piece_id = builder.editingBuilderMapId;
    if (!uid || !piece_id || piece_id === "local-map") return;
    const limits =
      meta.command_limits && Object.keys(meta.command_limits).length > 0
        ? meta.command_limits
        : null;
    await Promise.all([
      updatePieceMeta({
        piece_id,
        uid,
        name: meta.name,
        description: meta.description,
        cover: meta.cover,
        background: meta.background,
      }),
      updatePieceHints({ piece_id, uid, hints: meta.hint }),
      updatePieceCommands({
        piece_id,
        uid,
        enabled_commands: meta.enabled_commands,
        command_limits: limits,
      }),
    ]);
  };

/**
 * 画面を作り直すのを待っている区切りを、撮らずに残す（撮る前に画面を離れるとき）。
 * キャプチャは API がピースのいまのキャプチャで埋める
 */
export const flushPendingMilestones = () => (dispatch: AppDispatch, getState: () => RootState) => {
  getState().builder.pendingMilestones.forEach((pending) => {
    dispatch(recordEditHistory(pending.action_type, pending.payload));
    dispatch(actions.shiftMilestone());
  });
};

/** undo / redo の対象を、記録の操作の種類に直す（「もとにもどした：〇〇」の〇〇） */
const undoneOf = (record: ActionRecord): Record<string, unknown> => {
  switch (record.type) {
    case ActionCommandType.ADD_MODE:
      return { undone: "place_asset", name: record.forward.model?.name };
    case ActionCommandType.REMOVE_MODE:
      return { undone: "remove_asset", name: record.backward.model?.name };
    case ActionCommandType.CHANGE_MODE:
      return { undone: "change_asset" };
    case ActionCommandType.REPLACE_ALL:
      return { undone: record.label ?? "ai_generate" };
    default:
      return {};
  }
};

export const useActionCommand = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const commandManager = useRef(new CommandManager());

  // 記録に残す（中身は recordEditHistory）
  const recordHistory = useCallback(
    (action_type: string, payload?: Record<string, unknown>) =>
      dispatch(recordEditHistory(action_type, payload)),
    [dispatch],
  );

  // アクション実行（履歴記録付き）
  const execute = useCallback(
    (command: ActionCommand) => {
      switch (command.type) {
        case ActionCommandType.ADD_MODE: {
          dispatch(actions.addModelToMap(command.model));
          // Undo用に記録
          const record = commandManager.current.recordAdd(command.model);
          dispatch(actions.pushUndo(record));
          dispatch(actions.clearRedo());
          recordHistory("place_asset", { name: command.model.name });
          break;
        }
        case ActionCommandType.REMOVE_MODE: {
          // 削除前のモデルを取得してUndoに記録
          // Note: このフックではstoreにアクセスできないので、
          // モデル情報はコンポーネント側で渡す必要がある
          dispatch(actions.removeModelFromMap(command.meshId));
          recordHistory("remove_asset");
          break;
        }
        case ActionCommandType.CHANGE_MODE: {
          dispatch(
            actions.updateMapModel({
              id: command.id,
              changes: command.changes,
            }),
          );
          recordHistory("change_asset", { id: command.id });
          break;
        }
        default:
          break;
      }
    },
    [dispatch, recordHistory],
  );

  // Undo実行（共有された Redux スタックの末尾レコードを使う）
  const undo = useCallback(() => {
    const record = store.getState().builder.undoStacks.at(-1);
    if (!record) return null;

    // backward操作を実行
    switch (record.type) {
      case ActionCommandType.ADD_MODE:
        // ADDの取り消し = 削除
        if (record.backward.meshId) {
          dispatch(actions.removeModelFromMap(record.backward.meshId));
        }
        break;
      case ActionCommandType.REMOVE_MODE:
        // REMOVEの取り消し = 追加
        if (record.backward.model) {
          dispatch(actions.addModelToMap(record.backward.model));
        }
        break;
      case ActionCommandType.REPLACE_ALL:
        // マップ差し替えの取り消し = 差し替え前のマップに戻す（restore は大きさ・なまえなども）
        if (record.backward.models) {
          dispatch(
            actions.replaceMapState({
              models: record.backward.models,
              size: record.backward.size,
              meta: record.backward.meta,
            }),
          );
          if (record.backward.meta)
            dispatch(savePieceMeta(record.backward.meta)).catch(() => undefined);
        }
        break;
      case ActionCommandType.CHANGE_MODE:
        // CHANGEの取り消し = 前の値に戻す
        if (record.backward.id && record.backward.changes) {
          dispatch(
            actions.updateMapModel({
              id: record.backward.id,
              changes: record.backward.changes,
            }),
          );
        }
        break;
    }

    dispatch(actions.popUndo());
    dispatch(actions.pushRedo(record));
    // undo も記録に残す（記録の最新がいつも画面と一致するように）
    recordHistory("undo", undoneOf(record));

    return record;
  }, [dispatch, store, recordHistory]);

  // Redo実行（共有された Redux スタックの末尾レコードを使う）
  const redo = useCallback(() => {
    const record = store.getState().builder.redoStacks.at(-1);
    if (!record) return null;

    // forward操作を再実行
    switch (record.type) {
      case ActionCommandType.ADD_MODE:
        if (record.forward.model) {
          dispatch(actions.addModelToMap(record.forward.model));
        }
        break;
      case ActionCommandType.REMOVE_MODE:
        if (record.forward.meshId) {
          dispatch(actions.removeModelFromMap(record.forward.meshId));
        }
        break;
      case ActionCommandType.REPLACE_ALL:
        if (record.forward.models) {
          dispatch(
            actions.replaceMapState({
              models: record.forward.models,
              size: record.forward.size,
              meta: record.forward.meta,
            }),
          );
          if (record.forward.meta)
            dispatch(savePieceMeta(record.forward.meta)).catch(() => undefined);
        }
        break;
      case ActionCommandType.CHANGE_MODE:
        if (record.forward.id && record.forward.changes) {
          dispatch(
            actions.updateMapModel({
              id: record.forward.id,
              changes: record.forward.changes,
            }),
          );
        }
        break;
    }

    dispatch(actions.popRedo());
    dispatch(actions.pushUndo(record));
    recordHistory("redo", undoneOf(record));

    return record;
  }, [dispatch, store, recordHistory]);

  // 削除時に履歴記録（モデル情報付き）
  const executeRemove = useCallback(
    (model: MapModel) => {
      dispatch(actions.removeModelFromMap(model.id));
      const record = commandManager.current.recordRemove(model);
      dispatch(actions.pushUndo(record));
      dispatch(actions.clearRedo());
      recordHistory("remove_asset", { name: model.name });
    },
    [dispatch, recordHistory],
  );

  // 変更時に履歴記録（前後の値付き）
  const executeChange = useCallback(
    (
      id: string,
      newChanges: { [key: string]: unknown },
      oldChanges: { [key: string]: unknown },
    ) => {
      dispatch(
        actions.updateMapModel({
          id,
          changes: newChanges as { [key: string]: string },
        }),
      );
      const record = commandManager.current.recordChange(
        id,
        newChanges as { [key: string]: string },
        oldChanges as { [key: string]: string },
      );
      dispatch(actions.pushUndo(record));
      dispatch(actions.clearRedo());
      recordHistory("change_asset", { id });
    },
    [dispatch, recordHistory],
  );

  // Undo/Redo可能かチェック（共有された Redux スタックを参照）
  const canUndo = useCallback(() => store.getState().builder.undoStacks.length > 0, [store]);
  const canRedo = useCallback(() => store.getState().builder.redoStacks.length > 0, [store]);

  // 履歴クリア
  const clearHistory = useCallback(() => {
    commandManager.current.clear();
    dispatch(actions.clearHistory());
  }, [dispatch]);

  // 後方互換性：既存コードで handleAction(command) として使用可能
  const handleAction = execute;

  return {
    execute,
    handleAction, // 後方互換性のエイリアス
    executeRemove,
    executeChange,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  };
};
